import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Dialog, IconButton, DialogContent } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { BASE_URL } from "../../../api/axios";
import { EVENT_COLORS } from "../../../constants.js";

function Magnifier({ src, width = 450, zoom = 2, size = 300 }) {
  const [pos, setPos] = useState({ x: 0, y: 0, show: false, imgRect: null });
  const imgRef = useRef(null);

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setPos({ x, y, show: true, imgRect: rect });
  };

  const handleLeave = () => {
    setPos((p) => ({ ...p, show: false }));
  };

  useEffect(() => {
    if (imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect();
      setPos((p) => ({ ...p, imgRect: rect }));
    }
  }, [width]);

  const imgStyle = {
    width: width,
    height: "auto",
    display: "block",
    cursor: "crosshair",
  };

  return (
    <>
      <img
        ref={imgRef}
        src={src}
        alt="frame"
        style={imgStyle}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        onMouseEnter={handleMove}
        draggable={false}
      />

      {pos.show &&
        typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: Math.min(
                window.innerHeight - size - 10,
                (pos.imgRect?.top ?? 0) + pos.y - size / 2
              ),
              left: Math.min(
                window.innerWidth - size - 10,
                (pos.imgRect?.left ?? 0) + pos.x + 20
              ),
              zIndex: 9999,
              width: size,
              height: size,
              overflow: "hidden",
              borderRadius: 8,
              boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
              pointerEvents: "none",
              background: "#fff",
            }}
          >
            <img
              src={src}
              alt="magnified"
              style={{
                position: "absolute",
                width: (pos.imgRect?.width ?? width) * zoom,
                height: "auto",
                transform: `translate(-${pos.x * zoom - size / 2}px, -${
                  pos.y * zoom - size / 2
                }px)`,
                imageRendering: "auto",
                pointerEvents: "none",
              }}
            />
          </div>,
          document.body
        )}
    </>
  );
}

const ViewFramesModal = (props) => {
  const {
    selectedImage,
    handleClose,
    openModal,
    eventData,
    side = "side1",
  } = props;

  const normalize = (pred) => {
    if (!pred) return [];
    if (Array.isArray(pred)) return pred;
    if (pred.measurements && Array.isArray(pred.measurements))
      return pred.measurements;
    return [];
  };

  const tableDataSide1 = normalize(eventData?.side1_prediction);
  const tableDataSide2 = normalize(eventData?.side2_prediction);

  const tableData = side === "side2" ? tableDataSide2 : tableDataSide1;

  let error_threshold =
    side === "side2"
      ? eventData?.side2_prediction?.threshold
      : eventData?.side1_prediction?.threshold;
  
  let error_warn_threshold =
    side === "side2"
      ? eventData?.side2_prediction?.threshold_warn
      : eventData?.side1_prediction?.threshold_warn;
  
  if (!error_warn_threshold) {
    error_warn_threshold = error_threshold;
  }

  const display_image = `${BASE_URL}/defect-inspection/v1/${
    side === "side2" ? eventData?.side2_annotated_image : eventData?.side1_annotated_image
  }`;

  const handleCloseModal = () => {
    handleClose();
  };

  const BASE_WIDTH = 450; // base pixel width for the displayed image
  const [zoomLevel, setZoomLevel] = useState(1); // 1 = 100%
  const minZoom = 1; // enforce minimum zoom = original size (user requested)
  const maxZoom = 3;
  const zoomStep = 0.1;

  const wheelRef = useRef(null);
  const imgFallbackRef = useRef(null);

  useEffect(() => {
    const el = wheelRef.current;
    if (!el) return;

    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const fast = e.ctrlKey ? 3 : 1;
      if (e.deltaY < 0) {
        // zoom in
        setZoomLevel((z) =>
          Math.min(maxZoom, +(z + zoomStep * fast).toFixed(2))
        );
      } else if (e.deltaY > 0) {
        // zoom out but clamp to minZoom
        setZoomLevel((z) =>
          Math.max(minZoom, +(z - zoomStep * fast).toFixed(2))
        );
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });

    const imgEl = imgFallbackRef.current;
    if (imgEl && imgEl !== el) {
      imgEl.addEventListener("wheel", onWheel, { passive: false });
    }

    return () => {
      el.removeEventListener("wheel", onWheel);
      if (imgEl && imgEl !== el) {
        imgEl.removeEventListener("wheel", onWheel);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const displayWidth = Math.round(BASE_WIDTH * zoomLevel);

  return (
    <Dialog
      open={openModal}
      onClose={handleCloseModal}
      fullWidth
      maxWidth="lg"
      BackdropProps={{
        style: {
          backgroundColor: "transparent",
          boxShadow: "none",
          backdropFilter: "blur(0.4px)",
        },
      }}
      PaperProps={{
        style: {
          overflow: "hidden",
          boxShadow: "rgba(0, 0, 0, 0.1) 0px 1px 2px",
        },
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h3 style={{ padding: "34px 24px 24px 34px" }}>View Frames</h3>

        <IconButton
          edge="end"
          color="inherit"
          onClick={handleCloseModal}
          aria-label="close"
          style={{
            width: "24px",
            display: "flex",
            float: "right",
            padding: "25px",
            marginRight: "12px",
            marginTop: "16px",
          }}
        >
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent
        style={{ display: "flex", backgroundColor: "transparent" }}
      >
        {selectedImage ? (
          <>
            <div
              style={{ display: "flex", flexDirection: "column", zIndex: 1 }}
            >
              <div style={{ display: "flex" }}>
                <div
                  ref={wheelRef}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    padding: "12px",
                    width: "480px",
                    background: "transparent",
                    margin: "12px",
                  }}
                >
                  <div
                    style={{
                      border: "1px solid #ddd",
                      padding: 6,
                      background: "#000",
                    }}
                  >
                    <div ref={imgFallbackRef}>
                      <Magnifier
                        src={display_image}
                        width={displayWidth}
                        zoom={2}
                        size={300}
                      />
                    </div>
                  </div>
                </div>
                <div style={{ padding: "24px" }} />
              </div>
            </div>
          </>
        ) : (
          "NoRecords"
        )}

        {tableData && tableData.length > 0 && (
          <div className="table-container" style={{ marginTop: "14px" }}>
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Orientation</th>
                  <th>CAD</th>
                  <th>SYS</th>
                  <th>Difference (+/-)</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((rowData, index) => (
                  <tr key={index}>
                    <td>{rowData.name}</td>
                    <td>{rowData.orientation}</td>
                    <td>{rowData.length?.toFixed(3)}</td>
                    <td>{rowData.predicted_length?.toFixed(3)}</td>
                    <td>
                      {error_threshold ? (
                        error_warn_threshold && error_warn_threshold > Math.abs(rowData.length - rowData.predicted_length).toFixed(3)? 
                        (
                          <span
                            style={{
                              display: "inline",
                              backgroundColor: EVENT_COLORS.SUCCESS_SECONDARY_COLOR,
                              color: EVENT_COLORS.SUCCESS_COLOR,
                              borderRadius: "10px",
                              padding: "5px 10px",
                              textAlign: "center",
                            }}
                          >
                            {(
                              rowData.predicted_length - rowData.length
                            ).toFixed(3)}
                          </span>
                        ) : error_threshold > Math.abs(rowData.length - rowData.predicted_length).toFixed(3) ? 
                        (
                          <span
                            style={{
                              display: "inline",
                              backgroundColor: EVENT_COLORS.WARN_SECONDARY_COLOR,
                              color: EVENT_COLORS.WARN_COLOR,
                              borderRadius: "10px",
                              padding: "5px 10px",
                              textAlign: "center",
                            }}
                          >
                            {(
                              rowData.predicted_length - rowData.length
                            ).toFixed(3)}
                          </span>
                        ) : (
                          <span
                            style={{
                              display: "inline",
                              color: EVENT_COLORS.DEFECT_COLOR,
                              backgroundColor: EVENT_COLORS.DEFECT_SECONDARY_COLOR,
                              borderRadius: "10px",
                              padding: "5px 10px",
                              textAlign: "center",
                            }}
                          >
                            {(
                              rowData.predicted_length - rowData.length
                            ).toFixed(3)}
                          </span>
                        )
                      ) : (
                        <span
                          style={{
                            display: "inline",
                            backgroundColor: "#e9e9e9",
                            borderRadius: "10px",
                            padding: "5px 10px",
                            textAlign: "center",
                          }}
                        >
                          {(rowData.predicted_length - rowData.length).toFixed(
                            3
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>

      <div
        style={{
          display: "flex",
          margin: "12px",
          justifyContent: "center",
          alignItems: "center",
        }}
      />
    </Dialog>
  );
};

export default ViewFramesModal;
